// Studio dialogs: the spec editor (JSON with schema feedback, answer and explanation, saved through
// PATCH /puzzles/:id/spec and re-verified) and "Schedule to date" (POST /puzzles/:id/schedule with the
// Phase 0 lock rules and what gets displaced).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { missingCapabilities, PuzzleView, type PuzzleSpec } from "@lumen/puzzle-render";
import { usePuzzle } from "../../api/queries";
import {
  PuzzleSpecSchema,
  type AnswerValue,
  type PuzzleDetail,
  type PuzzleSource,
  type PuzzleStatus,
  type ReviewState,
  type VerificationResult,
  type VerificationSummary,
} from "../../api/schemas";
import { useDay, useEditSpec, useSchedulePuzzle } from "../../api/studio";
import type { BankVerifyResult, ScheduleTarget } from "../../api/studioSchemas";
import { useCan } from "../../auth/AuthContext";
import { Button, Chip, Dialog, Field, Loading, Segmented, StatusChip, TextInput, Select } from "../../components/ui";
import { formatLongDate } from "../../lib/dates";
import { firstSchedulableDay, optionLetterRefs, scheduleBlocker, verificationBadge } from "../../lib/studio";
import pages from "../pages.module.css";
import { Callout, StudioError, VerificationChip, VerificationDetails } from "./components";
import s from "./studio.module.css";

// ---------------------------------------------------------------- spec editor

type Problem = { kind: "error" | "warning" | "ok"; text: string };

function parseJson(text: string): { value: unknown; error: null } | { value: null; error: string } {
  try {
    return { value: JSON.parse(text) as unknown, error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    const at = /position (\d+)/.exec(message);
    if (at && !/line \d+/.test(message)) {
      const before = text.slice(0, Number(at[1]));
      return { value: null, error: `${message} (line ${before.split("\n").length}, column ${Number(at[1]) - before.lastIndexOf("\n")})` };
    }
    return { value: null, error: message };
  }
}

const optionIdsOf = (spec: unknown): { id: string; label: string }[] => {
  const opts = (spec as { answer?: { options?: { id?: unknown; glyph?: { kind?: string; value?: unknown } }[] } } | null)?.answer?.options;
  return Array.isArray(opts)
    ? opts
        .filter((o) => typeof o?.id === "string")
        .map((o) => ({ id: o.id as string, label: o.glyph?.kind === "text" && o.glyph.value !== undefined ? String(o.glyph.value) : (o.glyph?.kind ?? "") }))
    : [];
};

/** Only drafts in the bank, or on a day after UTC today + 1, take an edit (contracts/studio.md "Spec edits"). */
function editBlocker(p: PuzzleDetail["puzzle"]): string | null {
  if (p.status !== "draft") return `It is ${p.status}: only drafts can be edited. Move it back to draft first.`;
  if (p.mode && p.mode !== "bank" && (!p.date || p.date < firstSchedulableDay())) {
    return `It is scheduled for ${p.date}: only bank puzzles or days after UTC today + 1 can be edited.`;
  }
  return null;
}

export function SpecEditorDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = usePuzzle(id ?? undefined);
  if (!id) return null;
  return (
    <Dialog open title={<span>Edit spec · <span className="mono">{id}</span></span>} onClose={onClose} width={1100}>
      {q.isPending ? (
        <Loading rows={8} />
      ) : q.isError ? (
        <StudioError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <SpecEditor key={q.data.puzzle.id} puzzle={q.data.puzzle} onClose={onClose} />
      )}
    </Dialog>
  );
}

function SpecEditor({ puzzle, onClose }: { puzzle: PuzzleDetail["puzzle"]; onClose: () => void }) {
  const canEdit = useCan("studio.edit");
  const mutation = useEditSpec();
  const initial = useMemo(() => {
    const { check: _check, ...rest } = puzzle.spec as PuzzleSpec & { check?: unknown };
    void _check;
    return JSON.stringify(rest, null, 2);
  }, [puzzle.spec]);
  const [text, setText] = useState(initial);
  const [choice, setChoice] = useState(puzzle.answer.kind === "choice" ? puzzle.answer.value : "");
  const [cells, setCells] = useState(puzzle.answer.kind === "cellSet" ? JSON.stringify(puzzle.answer.value) : "[]");
  const [explanation, setExplanation] = useState(puzzle.explanation ?? "");
  const [result, setResult] = useState<BankVerifyResult | null>(null);

  const parsed = parseJson(text);
  const schema = parsed.error === null ? PuzzleSpecSchema.safeParse(parsed.value) : null;
  const spec = schema?.success ? schema.data : null;
  const answerKind = (spec?.answer as { kind?: string } | undefined)?.kind ?? puzzle.answer.kind;
  const optionIds = optionIdsOf(parsed.value);

  const problems: Problem[] = [];
  let answer: AnswerValue | null = null;
  if (parsed.error !== null) problems.push({ kind: "error", text: `JSON: ${parsed.error}` });
  else if (schema && !schema.success) {
    for (const issue of schema.error.issues.slice(0, 6)) problems.push({ kind: "error", text: `schema: ${issue.path.join(".") || "spec"}: ${issue.message}` });
  }
  if (spec) {
    if (spec.type !== puzzle.type) problems.push({ kind: "error", text: `type must stay ${puzzle.type} (got ${spec.type})` });
    if (answerKind === "choice") {
      if (!optionIds.some((o) => o.id === choice)) problems.push({ kind: "error", text: `answer: ${choice || "(none)"} is not one of the options (${optionIds.map((o) => o.id).join(", ")})` });
      else answer = { kind: "choice", value: choice };
    } else if (answerKind === "cellSet") {
      const c = parseJson(cells);
      const ok = c.error === null && Array.isArray(c.value) && c.value.every((p) => Array.isArray(p) && p.length === 2 && p.every((n) => Number.isInteger(n) && n >= 0));
      if (!ok) problems.push({ kind: "error", text: "answer: cells must be a JSON list of [row, col] pairs, e.g. [[0,1],[2,3]]" });
      else answer = { kind: "cellSet", value: c.value as [number, number][] };
    } else problems.push({ kind: "error", text: `answer: the panel can't edit a ${answerKind} answer` });
    for (const m of missingCapabilities(spec)) problems.push({ kind: "warning", text: `the web renderer can't draw ${m} (a new component can't ship server-only)` });
  }
  if (explanation.length > 2000) problems.push({ kind: "error", text: `explanation: ${explanation.length} of 2000 characters` });
  const letters = optionLetterRefs(explanation);
  if (letters.length) problems.push({ kind: "warning", text: `explanation mentions option letters (${letters.join(", ")}): the engine fails it, since players see their own option order` });
  const blocking = problems.some((p) => p.kind === "error");
  if (!blocking) problems.unshift({ kind: "ok", text: "JSON parses and matches the PuzzleSpec envelope. The engine runs the full schema and the solver on save." });

  const blocker = editBlocker(puzzle);
  const dirty = text !== initial || explanation !== (puzzle.explanation ?? "") || JSON.stringify(answer) !== JSON.stringify(puzzle.answer);

  const save = () => {
    if (!answer || parsed.error !== null) return;
    mutation.mutate({ id: puzzle.id, edit: { spec: parsed.value, answer, explanation } }, { onSuccess: (r) => setResult(r) });
  };

  return (
    <div className={s.dialogStack} data-testid="spec-editor">
      {blocker ? (
        <Callout tone="warning" title="This puzzle can't be edited now">
          <p>{blocker}</p>
        </Callout>
      ) : null}
      <div className={s.editorGrid}>
        <Field label="Spec (JSON)">
          <textarea
            className={s.editorText}
            value={text}
            spellCheck={false}
            aria-invalid={parsed.error !== null || (schema !== null && !schema.success)}
            aria-label="Spec JSON"
            onChange={(e) => {
              setText(e.target.value);
              setResult(null);
            }}
          />
        </Field>
        <div className={s.editorSide}>
          <ul className={s.problems} data-testid="spec-problems" aria-live="polite">
            {problems.map((p, i) => (
              <li key={i} data-kind={p.kind}>
                {p.text}
              </li>
            ))}
          </ul>
          <div className={s.previewBox}>{spec ? <PuzzleView spec={spec} answer={answer} reveal compact /> : <p className={pages.faint}>The preview appears when the JSON is valid.</p>}</div>
          {answerKind === "choice" ? (
            <Field label="Answer">
              <Select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Answer option">
                {!optionIds.some((o) => o.id === choice) ? <option value={choice}>{choice || "—"}</option> : null}
                {optionIds.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.id}
                    {o.label ? ` · ${o.label}` : ""}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field label="Answer cells [row, col]">
              <TextInput value={cells} onChange={(e) => setCells(e.target.value)} aria-label="Answer cells" className="mono" />
            </Field>
          )}
          <Field label="Explanation" hint="Shown after the puzzle closes. No option letters: players see their own order.">
            <textarea className={s.explain} value={explanation} onChange={(e) => setExplanation(e.target.value)} aria-label="Explanation" maxLength={2400} />
          </Field>
        </div>
      </div>

      <StudioError error={mutation.error} />
      {result ? (
        <Callout
          tone={result.verification.ok ? "success" : "warning"}
          title={result.verification.ok ? "Saved · the engine verified the edit" : "Saved · the edit fails verification"}
          testId="edit-result"
        >
          <div className={s.chipsRow}>
            <VerificationChip badge={verificationBadge(result.verification, result.puzzle.reviewState, result.puzzle.source)} />
            {result.puzzle.reviewRequired ? <Chip tone="warning">review {result.puzzle.reviewState}</Chip> : null}
          </div>
          <VerificationDetails v={result.verification} />
          {!result.verification.ok ? <p>It stays in the bank with this verification, which blocks scheduling until an edit passes.</p> : null}
        </Callout>
      ) : null}

      <div className={pages.rowActions} style={{ justifyContent: "flex-end" }}>
        <Button variant="ghost" onClick={onClose}>
          {result ? "Done" : "Cancel"}
        </Button>
        {canEdit ? (
          <Button variant="primary" onClick={save} busy={mutation.isPending} disabled={blocking || Boolean(blocker) || !dirty}>
            Save and re-verify
          </Button>
        ) : (
          <span className={pages.readOnly}>Read-only · editing needs the editor role</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- schedule to date

export interface ScheduleSubject {
  id: string;
  type: string;
  status: PuzzleStatus;
  mode?: string;
  source?: PuzzleSource;
  reviewState?: ReviewState;
  verification?: VerificationSummary | VerificationResult | null;
}

const LOCKED: PuzzleStatus[] = ["approved", "published", "retired"];

export function ScheduleDialog({ puzzle, onClose }: { puzzle: ScheduleSubject | null; onClose: () => void }) {
  if (!puzzle) return null;
  return (
    <Dialog open title={<span>Schedule <span className="mono">{puzzle.id}</span></span>} onClose={onClose} width={560}>
      <ScheduleBody key={puzzle.id} puzzle={puzzle} onClose={onClose} />
    </Dialog>
  );
}

function ScheduleBody({ puzzle, onClose }: { puzzle: ScheduleSubject; onClose: () => void }) {
  const first = firstSchedulableDay();
  const [target, setTarget] = useState<ScheduleTarget>("daily");
  const [date, setDate] = useState(first);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const day = useDay(target, valid ? date : null);
  const mutation = useSchedulePuzzle();
  const navigate = useNavigate();
  const occupant = day.data?.items[0] ?? null;
  const gate = scheduleBlocker(puzzle.verification, puzzle.reviewState, puzzle.source);
  const statusBlock = puzzle.mode && puzzle.mode !== "bank" ? `It is already scheduled (${puzzle.mode}).` : puzzle.status !== "draft" && puzzle.status !== "approved" ? `It is ${puzzle.status}: only draft or approved bank puzzles can be scheduled.` : null;
  const result = mutation.data;

  if (result) {
    return (
      <div className={s.dialogStack} data-testid="schedule-result">
        <Callout tone="success" title={`Scheduled for ${result.puzzle.date ? formatLongDate(result.puzzle.date) : date}`}>
          <p>
            <code>{result.puzzle.id}</code> is the {result.puzzle.mode === "hard" ? "Hard Mode" : "daily"} puzzle
            {result.puzzle.puzzleNumber ? ` #${result.puzzle.puzzleNumber}` : ""}, still <strong>{result.puzzle.status}</strong>
            {result.puzzle.status === "draft" ? ": approve it like any other day before it publishes." : "."}
          </p>
          <p>{result.displaced ? <>The day&rsquo;s previous puzzle <code>{result.displaced.id}</code> ({result.displaced.status}) went back to the bank.</> : "The day was empty: nothing was displaced."}</p>
        </Callout>
        <div className={pages.rowActions} style={{ justifyContent: "flex-end" }}>
          {result.puzzle.mode === "daily" ? (
            <Button variant="ghost" onClick={() => navigate("/schedule")}>
              Open the schedule
            </Button>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.dialogStack} data-testid="schedule-dialog">
      <div className={s.chipsRow}>
        <span className="mono">{puzzle.type}</span>
        <StatusChip status={puzzle.status} />
        <VerificationChip badge={verificationBadge(puzzle.verification, puzzle.reviewState, puzzle.source)} />
      </div>
      {gate || statusBlock ? (
        <Callout tone="warning" title="The API will refuse this" code={statusBlock ? "409 conflict" : "409 not_verified"} testId="schedule-gate">
          <p>{statusBlock ?? gate}</p>
        </Callout>
      ) : null}
      <div className={pages.rowActions} style={{ alignItems: "flex-start", gap: 14 }}>
        <div className={s.fieldCol}>
          <span className="monoLabel">Calendar</span>
          <Segmented<ScheduleTarget>
            label="Calendar"
            value={target}
            onChange={setTarget}
            options={[
              { value: "daily", label: "Daily" },
              { value: "hard", label: "Hard Mode" },
            ]}
          />
        </div>
        <Field label="Date (UTC)" hint={`${first} or later`}>
          <TextInput type="date" value={date} min={first} onChange={(e) => setDate(e.target.value)} aria-label="Date" />
        </Field>
      </div>

      <div className={s.occupant} data-testid="occupant" data-state={day.isPending ? "loading" : occupant ? occupant.status : "empty"}>
        <span className={s.sectionTitle}>What the day holds now</span>
        {!valid ? (
          <span className={pages.faint}>Pick a date.</span>
        ) : day.isPending ? (
          <span className={pages.faint}>Checking {date}…</span>
        ) : day.isError ? (
          <StudioError error={day.error} />
        ) : !occupant ? (
          <span>Nothing is scheduled: {puzzle.id} takes the empty day.</span>
        ) : (
          <>
            <span className={s.occupantHead}>
              <code>{occupant.id}</code>
              <StatusChip status={occupant.status} />
              <span className="mono">{occupant.type}</span>
              {occupant.puzzleNumber ? <span className="mono">#{occupant.puzzleNumber}</span> : null}
            </span>
            {LOCKED.includes(occupant.status) ? (
              <span className={s.locked}>Locked: an {occupant.status} day is never replaced. The API answers 409.</span>
            ) : (
              <span>It goes back to the bank, keeping its id and status ({occupant.status}).</span>
            )}
          </>
        )}
      </div>

      <div>
        <span className={s.sectionTitle}>Phase 0 lock rules</span>
        <ul className={s.rules}>
          <li>Only days after UTC today + 1 ({first} or later).</li>
          <li>The day&rsquo;s draft or rejected puzzle returns to the bank; approved, published and retired days are never replaced.</li>
          <li>Solver types need their verification ok; types without a solver need an approved review.</li>
          <li>The puzzle keeps its id and status: a draft still needs approval before its day.</li>
        </ul>
      </div>

      <StudioError error={mutation.error} />

      <div className={pages.rowActions} style={{ justifyContent: "flex-end" }}>
        <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="primary" busy={mutation.isPending} disabled={!valid} onClick={() => mutation.mutate({ id: puzzle.id, target, date })}>
          Schedule to {valid ? date : "date"}
        </Button>
      </div>
    </div>
  );
}
